import { TreeMap } from './utils/tree';

/// Shared cache entry interface for Entry and Tombstone.
interface CacheEntry<V> {
  value: V;
  lastEventId: number;
  isValid: boolean;
  isLeftBound: boolean;
  isRightBound: boolean;
}

class Entry<V> implements CacheEntry<V> {
  _data: V;
  lastEventId: number;
  isLeftBound: boolean;
  isRightBound: boolean;

  constructor(value: V, lastEventId: number, isLeft: boolean = false, isRight: boolean = false) {
    this._data = value;
    this.lastEventId = (lastEventId || 0);

    this.isLeftBound = isLeft;
    this.isRightBound = isRight;
  }

  get isValid(): boolean {
    return true;
  }

  get value(): V {
    return this._data;
  }
}

class Tombstone<V> implements CacheEntry<V> {
  lastEventId: number;
  isLeftBound: boolean;
  isRightBound: boolean;

  constructor(lastEventId: number) {
    this.lastEventId = lastEventId;

    this.isLeftBound = false;
    this.isRightBound = false;
  }

  get isValid(): boolean {
    return false;
  }

  get value(): V {
    return null;
  }
}

/// All values suitable for caching must conform to this interface.
interface CachedValue<Key> {
  cacheKey: Key;
  lastEventId: number;
}

/// A cache for ranges of items in a collection.
/// Keeps track of the left and right bounds of the entire collection.
/// Keeps track of the left and right bounds of the pages of items.
class ItemsCache<Key, V extends CachedValue<Key>> {
  public readonly items: TreeMap<Key, CacheEntry<V>> = new TreeMap<Key, CacheEntry<V>>();
  private beginKey: Key | null = null;
  private endKey: Key | null = null;

  constructor() {
    this.items.clear(); // Set count to 0 instead of null.
  }

  /*
   * Used in tests.
   * @internal
   */
  get size(): number {
    return this.items.size;
  }

  get collectionBegin(): Key {
    return this.beginKey;
  }

  get collectionEnd(): Key {
    return this.endKey;
  }

  has(key: Key): boolean {
    let cacheEntry = this.items.get(key);
    return cacheEntry?.isValid ?? false;
  }

  // Whether an entry exists in the cache either as a value or a tombstone.
  exists(key: Key): boolean {
    let cacheEntry = this.items.get(key);
    return !!cacheEntry;
  }

  // Return only cached value or null if it is missing or marked with a tombstone.
  getValue(key: Key): V {
    let cacheEntry = this.items.get(key);
    return cacheEntry?.value ?? null;
  }

  // Return a cache entry, can either be missing (null), valid or a tombstone.
  get(key: Key): CacheEntry<V> | null {
    return this.items.get(key);
  }

  // Get an item next to the given key in specified search order.
  getNextItem(key: Key | null, order: 'asc' | 'desc', inclusive: boolean): V | null {
    if (key === null) {
      return this.getFirstItem(order);
    }
    if (inclusive) {
      return this.getValue(key);
    }
    return this.getAdjacentItem(key, order);
  }

  // Find the first item in the cache based on query order.
  // @internal
  getFirstItem(order: 'asc' | 'desc'): V | null {
    const key = (order === 'asc') ? this.beginKey : this.endKey;
    if (this.exists(key)) {
      const entry = this.items.get(key);
      if (entry.isValid) {
        return entry.value;
      }
      return this.getAdjacentItem(key, order);
    }
    return null;
  }

  // Find the first valid item next to the given one.
  // @internal
  getAdjacentItem(key: Key, order: 'asc' | 'desc'): V | null {
    const iterator = (order === 'asc') ? this.items.getIterator(key) : this.items.getReverseIterator(key);
    for (const [k, v] of iterator) {
      if ((order === 'asc' ? v.isRightBound : v.isLeftBound)) {
        break;
      }

      if (v.isValid) {
        return v.value;
      }
    }
    return null;
  }

  store(key: Key, data: V, lastEventId: number, isLeft: boolean = true, isRight: boolean = true): V | null {
    let cacheEntry = this.items.get(key);
    if (cacheEntry && cacheEntry.lastEventId > lastEventId) {
      return cacheEntry.value;
    }

    this.items.set(key, new Entry<V>(data, lastEventId,
        cacheEntry ? cacheEntry.isLeftBound && isLeft : isLeft,
        cacheEntry ? cacheEntry.isRightBound && isRight : isRight
    ));

    // Update the `begin` and `end` markers when new items extend beyond current bounds.
    // IMPORTANT: Use `!== null` instead of falsy check because keys can be 0 (which is falsy).
    // Also clear the boundary flag on the old boundary item when extending.
    if (this.beginKey !== null && key < this.beginKey) {
      const oldBegin = this.items.get(this.beginKey);
      if (oldBegin) {
        oldBegin.isLeftBound = false;
      }
      this.beginKey = key;
    }

    if (this.endKey !== null && this.endKey < key) {
      const oldEnd = this.items.get(this.endKey);
      if (oldEnd) {
        oldEnd.isRightBound = false;
      }
      this.endKey = key;
    }

    return data;
  }

  // Populate a contiguous range of items from a paginator page.
  populate(entries: V[], isBegin: boolean, isEnd: boolean): void {
    if (!entries.length) {
      return;
    }

    const newEntries = [...entries];

    // Backend-compatible lexicographical key sort
    newEntries.sort((x1, x2) => {
      if (x1.cacheKey === x2.cacheKey) {
        return 0;
      }
      if (x1.cacheKey > x2.cacheKey) {
        return 1;
      }
      return -1;
    });

    newEntries.forEach(((item, index) => {
      this.store(item.cacheKey, item, item.lastEventId, index === 0, index === newEntries.length - 1);
    }));

    if (isBegin) {
      const firstKey = newEntries[0].cacheKey;
      if (!this.beginKey || firstKey < this.beginKey) {
        this.beginKey = firstKey;
      }
    }
    if (isEnd) {
      const lastKey = newEntries[newEntries.length - 1].cacheKey;
      if (!this.endKey || this.endKey < lastKey) {
        this.endKey = lastKey;
      }
    }

  }

  delete(key: Key, lastEventId: number | null): void {
    let cacheEntry = this.items.get(key);
    /* Force delete when revision is unknown */
    if (cacheEntry === null || lastEventId === null || cacheEntry.lastEventId < lastEventId) {
      this.items.set(key, new Tombstone<V>(lastEventId));
    }
  }

  // Shall we ignore the item with this key in an update?
  isKnown(key: Key, lastEventId: number): boolean {
    let cacheEntry = this.items.get(key);
    return cacheEntry && cacheEntry.lastEventId >= lastEventId;
  }

  *getIterator(key: Key) {
    if (key === null && this.beginKey !== null) {
      key = this.beginKey;
    }
    if (key === null) {
      return;
    }
    for (let [k, v] of this.items.getIterator(key)) {
      if (!v.isValid) {
        continue;
      }

      yield v;

      if (v.isRightBound) {
        break;
      }
    }
  }

  *getReverseIterator(key: Key) {
    if (key === null && this.endKey !== null) {
      key = this.endKey;
    }
    if (key === null) {
      return;
    }
    for (let [k, v] of this.items.getReverseIterator(key)) {
      if (!v.isValid) {
        continue;
      }

      yield v;

      if (v.isLeftBound) {
        break;
      }
    }
  }

  forEach(callbackfn: (key: Key, value: V) => void): void {
    if (this.items) {
      for (let [key, entry] of this.items) {
        if (entry.isValid) {
          callbackfn(key, entry.value);
        }
      }
    }
  }
}

export { ItemsCache };
export default ItemsCache;
